import React, { useState, useEffect } from 'react';
import { FileText, Download, Search, Filter, Clock, CheckCircle2, User } from 'lucide-react';
import { API_BASE as API } from '../../lib/apiBase';


export default function LegalArchivePanel({ token, lang }) {
  const [acceptances, setAcceptances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDoc, setFilterDoc] = useState('all');

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/admin/legal-archive`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { setAcceptances(data.acceptances || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const filtered = acceptances.filter(a => {
    if (filterDoc !== 'all' && a.document_slug !== filterDoc) return false;
    if (search && !a.user_name?.toLowerCase().includes(search.toLowerCase()) && !a.user_email?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const docTypes = [...new Set(acceptances.map(a => a.document_slug))];

  return (
    <div data-testid="legal-archive">
      <h2 className="font-heading font-bold text-2xl mb-2 flex items-center gap-2">
        <FileText size={22} className="text-pulse-primary" />
        {lang === 'es' ? 'Archivo Legal' : 'Legal Archive'}
      </h2>
      <p className="text-xs text-pulse-muted mb-6">
        {lang === 'es'
          ? 'Registro completo de todas las aceptaciones legales. Cada entrada incluye timestamp, IP, version del documento y datos del firmante.'
          : 'Complete record of all legal acceptances. Each entry includes timestamp, IP, document version and signer data.'}
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-pulse-muted" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={lang === 'es' ? 'Buscar por nombre o email...' : 'Search by name or email...'}
            className="w-full bg-pulse-surface border border-pulse-border rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-1 focus:ring-cyan-500 focus:outline-none"
          />
        </div>
        <select
          value={filterDoc} onChange={e => setFilterDoc(e.target.value)}
          className="bg-pulse-surface border border-pulse-border rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">{lang === 'es' ? 'Todos los documentos' : 'All documents'}</option>
          {docTypes.map(d => <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      <div className="bg-pulse-surface border border-pulse-border rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 px-4 py-2 bg-pulse-elevated text-[10px] text-pulse-muted uppercase tracking-wider font-medium">
          <span>{lang === 'es' ? 'Usuario' : 'User'}</span>
          <span>{lang === 'es' ? 'Documento' : 'Document'}</span>
          <span>{lang === 'es' ? 'Fecha' : 'Date'}</span>
          <span>IP</span>
        </div>

        {loading ? (
          <div className="p-8 text-center text-pulse-muted text-sm">
            {lang === 'es' ? 'Cargando archivo legal...' : 'Loading legal archive...'}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-pulse-muted text-sm">
            {lang === 'es' ? 'No hay registros' : 'No records'}
          </div>
        ) : (
          <div className="divide-y divide-pulse-border max-h-[500px] overflow-y-auto">
            {filtered.map((a, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 px-4 py-3 hover:bg-pulse-elevated/50 transition-colors items-center">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{a.user_name || 'Unknown'}</p>
                  <p className="text-[10px] text-pulse-muted truncate">{a.user_email || ''}</p>
                </div>
                <div>
                  <span className="text-xs px-2 py-0.5 bg-cyan-500/10 text-cyan-400 rounded-full">
                    {(a.document_slug || '').replace(/_/g, ' ')}
                  </span>
                  <p className="text-[9px] text-pulse-muted mt-0.5">v{a.version || '1'}</p>
                </div>
                <div>
                  <p className="text-xs">{a.accepted_at ? new Date(a.accepted_at).toLocaleDateString() : '-'}</p>
                  <p className="text-[9px] text-pulse-muted">{a.accepted_at ? new Date(a.accepted_at).toLocaleTimeString() : ''}</p>
                </div>
                <div className="text-[9px] text-pulse-muted font-mono">{a.ip_address || '-'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[9px] text-pulse-muted mt-3 text-center">
        {lang === 'es'
          ? `${filtered.length} registros. Este archivo se conserva como prueba de consentimiento (RGPD Art. 7.1).`
          : `${filtered.length} records. This archive is kept as proof of consent (GDPR Art. 7.1).`}
      </p>
    </div>
  );
}
